import { useMemo, useState } from "react";
import { playNow, kickAudio } from "@/audio/piano";
import { spellDegree, parseNote, toAscii, spellPc } from "@/theory/spell";
import { pretty } from "@/theory/notes";

/**
 * ChordBuilder — explore how chords stack from a root.
 *
 * Walks through the four families this course covers:
 *   • Dyads      — 2 notes  (root + interval)
 *   • Triads     — 3 notes  (major / minor / diminished / augmented / sus2 / sus4)
 *   • Sevenths   — 4 notes  (maj7, 7, m7, m7♭5, dim7)
 *   • Extensions — 5+ notes (9, 11, 13, with optional ♯/♭ alterations)
 *
 * Each chord can be heard. Used in lessons 5, 15, 19, 20, 21, 22, 27, 30, 31.
 */

type Family = "dyad" | "triad" | "seventh" | "extension";

interface ChordSpec {
  name: string;
  shortLabel: string;
  intervals: number[]; // semitones from root
  /** Degree labels matching `intervals`, so each tone is spelled by its letter. */
  degrees: string[];
  description: string;
}

const CHORD_LIBRARY: Record<Family, ChordSpec[]> = {
  dyad: [
    { name: "Perfect Fifth (Power)", shortLabel: "5", intervals: [0, 7], degrees: ["1", "5"], description: "Root + perfect 5th. The 'open' chord. Neutral — major or minor work over it." },
    { name: "Major Third",           shortLabel: "M3", intervals: [0, 4], degrees: ["1", "3"], description: "Root + major 3rd. Bright, half a major chord." },
    { name: "Minor Third",           shortLabel: "m3", intervals: [0, 3], degrees: ["1", "b3"], description: "Root + minor 3rd. Sad, half a minor chord." },
    { name: "Octave",                shortLabel: "8ve", intervals: [0, 12], degrees: ["1", "8"], description: "Root + octave. Same note, different register — fattens the bass." },
  ],
  triad: [
    { name: "Major",      shortLabel: "M",   intervals: [0, 4, 7], degrees: ["1", "3", "5"], description: "Root, major 3rd, perfect 5th. Bright, happy, victorious." },
    { name: "Minor",      shortLabel: "m",   intervals: [0, 3, 7], degrees: ["1", "b3", "5"], description: "Root, minor 3rd, perfect 5th. Serious, melancholic." },
    { name: "Diminished", shortLabel: "dim", intervals: [0, 3, 6], degrees: ["1", "b3", "b5"], description: "Root, minor 3rd, diminished 5th. Tense, unresolved." },
    { name: "Augmented",  shortLabel: "aug", intervals: [0, 4, 8], degrees: ["1", "3", "#5"], description: "Root, major 3rd, augmented 5th. Eerie, suspended in space." },
    { name: "Sus2",       shortLabel: "sus2", intervals: [0, 2, 7], degrees: ["1", "2", "5"], description: "Root, 2nd, 5th. Open and unresolved — the 3rd is replaced." },
    { name: "Sus4",       shortLabel: "sus4", intervals: [0, 5, 7], degrees: ["1", "4", "5"], description: "Root, 4th, 5th. Wants to resolve down to the major 3rd." },
  ],
  seventh: [
    { name: "Major 7th",       shortLabel: "maj7", intervals: [0, 4, 7, 11], degrees: ["1", "3", "5", "7"], description: "Major triad + major 7th. Lush, jazzy, cinematic." },
    { name: "Dominant 7th",    shortLabel: "7",    intervals: [0, 4, 7, 10], degrees: ["1", "3", "5", "b7"], description: "Major triad + minor 7th. The blues / dominant tension chord." },
    { name: "Minor 7th",       shortLabel: "m7",   intervals: [0, 3, 7, 10], degrees: ["1", "b3", "5", "b7"], description: "Minor triad + minor 7th. Smooth, soulful." },
    { name: "Half-diminished", shortLabel: "m7♭5", intervals: [0, 3, 6, 10], degrees: ["1", "b3", "b5", "b7"], description: "Diminished triad + minor 7th. The 'ii' chord in minor keys." },
    { name: "Fully diminished", shortLabel: "dim7", intervals: [0, 3, 6, 9], degrees: ["1", "b3", "b5", "bb7"], description: "Stack of minor 3rds. Maximum tension." },
    { name: "Minor major 7th",  shortLabel: "mM7", intervals: [0, 3, 7, 11], degrees: ["1", "b3", "5", "7"], description: "Minor triad + major 7th. Spy-thriller chord — used in James Bond intro." },
  ],
  extension: [
    { name: "Major 9th",       shortLabel: "maj9", intervals: [0, 4, 7, 11, 14], degrees: ["1", "3", "5", "7", "9"], description: "Maj7 + 9. Very lush — used in jazz ballads and modern pop." },
    { name: "Dominant 9th",    shortLabel: "9",    intervals: [0, 4, 7, 10, 14], degrees: ["1", "3", "5", "b7", "9"], description: "Dom 7 + 9. Funk and soul standard." },
    { name: "Minor 11th",      shortLabel: "m11",  intervals: [0, 3, 7, 10, 14, 17], degrees: ["1", "b3", "5", "b7", "9", "11"], description: "m7 + 9 + 11. Floating, dreamy, used in modal jazz." },
    { name: "Major 13th",      shortLabel: "maj13", intervals: [0, 4, 7, 11, 14, 21], degrees: ["1", "3", "5", "7", "9", "13"], description: "Maj7 + 9 + 13. Maximum harmonic colour while still sounding major." },
    { name: "7♭9",             shortLabel: "7♭9",  intervals: [0, 4, 7, 10, 13], degrees: ["1", "3", "5", "b7", "b9"], description: "Dom 7 with a flat 9. Spanish, exotic, very tense." },
    { name: "7♯11",            shortLabel: "7♯11", intervals: [0, 4, 7, 10, 18], degrees: ["1", "3", "5", "b7", "#11"], description: "Lydian-dominant flavour — the bright, floating dominant of film scores and Steely Dan." },
  ],
};

interface ChordBuilderProps {
  /** Root note name without octave (default C). */
  root?: string;
  /** Which families to display (default: triad). */
  families?: Family[];
  /** Octave to play the root in (default 4). */
  octave?: number;
}

const ROOTS = ["C", "D", "E", "F", "G", "A", "B"];

export function ChordBuilder({
  root: initialRoot = "C",
  families = ["triad"],
  octave = 4,
}: ChordBuilderProps) {
  const [root, setRoot] = useState(initialRoot);
  const [activeFamily, setActiveFamily] = useState<Family>(families[0]);

  // parseNote handles "C", "Bb", "B♭" and "F#" alike. The old lookup returned
  // -1 for any flat root — and because -1 is not null, the `?? 0` guard never
  // fired, so the tiles rendered "undefined".
  const rootSemi = useMemo(() => parseNote(root)?.pc ?? 0, [root]);

  /** Spell chord tone `i` by its degree: E augmented is E–G♯–B♯, not E–G♯–C. */
  const noteName = (c: ChordSpec, i: number) =>
    spellDegree(root, c.degrees[i]) ?? pretty(spellPc(rootSemi + c.intervals[i], false));

  const playChord = (c: ChordSpec) => {
    kickAudio();
    const pitches = c.intervals.map((iv, i) => {
      const o = octave + Math.floor((rootSemi + iv) / 12);
      return `${toAscii(noteName(c, i))}${o}`;
    });
    void playNow(pitches, "2n", 0.7);
  };

  const chords = CHORD_LIBRARY[activeFamily];

  return (
    <div className="my-5 rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-500/[0.05] to-amber-500/[0.03] p-5">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-purple-300">
            Chord builder
          </div>
          <div className="mt-1 font-mt text-xl text-nsm-ink">
            Root: <span className="font-semibold text-purple-300">{root}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {ROOTS.map((r) => (
            <button
              key={r}
              onClick={() => setRoot(r)}
              className={`min-w-[32px] rounded px-2 py-1.5 text-[12px] font-semibold transition ${
                r === root
                  ? "bg-purple-500/30 text-purple-100"
                  : "bg-zinc-800/50 text-nsm-dim hover:text-nsm-ink"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Family tabs */}
      {families.length > 1 && (
        <div className="mb-3 flex flex-wrap gap-1">
          {families.map((f) => (
            <button
              key={f}
              onClick={() => setActiveFamily(f)}
              className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider transition ${
                f === activeFamily
                  ? "bg-amber-500 text-zinc-950"
                  : "bg-zinc-800/50 text-nsm-dim hover:text-nsm-ink"
              }`}
            >
              {f === "dyad" ? "Dyads" : f === "triad" ? "Triads" : f === "seventh" ? "7ths" : "Extensions"}
            </button>
          ))}
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        {chords.map((c) => (
          <button
            key={c.name}
            onClick={() => playChord(c)}
            className="group rounded-xl border border-nsm-border bg-zinc-900/50 p-3 text-left transition hover:border-amber-500/40 hover:bg-zinc-900/80"
          >
            <div className="flex items-baseline justify-between">
              <div className="font-mt text-lg font-semibold text-nsm-ink">
                {root}
                <span className="text-amber-300">{c.shortLabel}</span>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-nsm-dim opacity-0 transition group-hover:opacity-100">
                ▶ play
              </span>
            </div>
            <div className="mt-1 flex flex-wrap gap-1 text-[11px] font-mono text-amber-200">
              {c.intervals.map((_, i) => (
                <span key={i} className="rounded bg-amber-500/10 px-1.5 py-0.5">
                  {noteName(c, i)}
                </span>
              ))}
            </div>
            <div className="mt-2 text-[12px] leading-snug text-nsm-dim">
              {c.description}
            </div>
            <div className="mt-1 text-[10px] uppercase tracking-wider text-nsm-dim">
              {c.name}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
